import { useEffect, useState, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Camera, X, Image as ImageIcon } from "lucide-react";
import { PROPERTIES } from "@/constants/testIds";

const empty = {
  name: "", address: "", country: "AR", city: "", currency: "ARS", company: "",
  property_type: "apartment", status: "occupied", value: 0, photo_url: "",
  latitude: null, longitude: null, notes: "",
};

export default function PropertyDialog({ open, onOpenChange, property, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState("");
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    setForm(property ? { ...empty, ...property } : empty);
    setPendingFile(null);
    setPendingPreview("");
  }, [property, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhotoSelected = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten imágenes");
      return;
    }
    // If property already exists, upload immediately
    if (property?.id) {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post(`/properties/${property.id}/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        set("photo_gridfs_id", r.data.photo_gridfs_id);
        set("photo_url", "");
        toast.success("Foto subida");
        onSaved?.();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally { setUploading(false); }
    } else {
      // Otherwise hold it until property is created
      setPendingFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setPendingPreview(ev.target?.result || "");
      reader.readAsDataURL(file);
      toast.success("Foto lista. Se subirá al guardar la propiedad.");
    }
  };

  const clearPhoto = async () => {
    if (pendingFile) { setPendingFile(null); setPendingPreview(""); return; }
    if (!property?.id) { set("photo_gridfs_id", ""); set("photo_url", ""); return; }
    try {
      await api.delete(`/properties/${property.id}/photo`);
      set("photo_gridfs_id", ""); set("photo_url", "");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        value: parseFloat(form.value) || 0,
        latitude: form.latitude === "" ? null : form.latitude,
        longitude: form.longitude === "" ? null : form.longitude,
      };
      let saved;
      if (property?.id) {
        const r = await api.put(`/properties/${property.id}`, payload);
        saved = r.data;
        toast.success("Propiedad actualizada");
      } else {
        const r = await api.post("/properties", payload);
        saved = r.data;
        toast.success("Propiedad creada");
      }
      // If we have a pending photo, upload it now
      if (pendingFile && saved?.id) {
        const fd = new FormData();
        fd.append("file", pendingFile);
        try {
          await api.post(`/properties/${saved.id}/photo`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success("Foto subida");
        } catch (err) {
          toast.error("Propiedad guardada, pero falló la subida de foto: " + (formatApiError(err.response?.data?.detail) || err.message));
        }
        setPendingFile(null);
        setPendingPreview("");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={PROPERTIES.dialog} className="max-w-2xl max-h-[88vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-heading">{property ? "Editar propiedad" : "Nueva propiedad"}</DialogTitle>
          <DialogDescription>Complete los datos de la propiedad. La moneda se usa para el valor y transacciones.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Nombre</Label>
            <Input data-testid="property-form-name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
          </div>
          <div className="md:col-span-2">
            <Label>Dirección</Label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} required />
          </div>

          <div className="md:col-span-2 rounded-xl border-2 border-emerald-500/40 bg-emerald-500/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] uppercase tracking-widest text-emerald-500 font-semibold">💰 Valor de mercado</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3 items-end">
              <div>
                <Label className="text-xs text-muted-foreground">Precio estimado de la propiedad</Label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="property-form-value"
                  value={form.value}
                  onChange={(e) => set("value", e.target.value)}
                  placeholder="Ej: 120000"
                  className="text-xl font-heading font-semibold h-12"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Moneda</Label>
                <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger className="h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="ARS">ARS</SelectItem>
                    <SelectItem value="MXN">MXN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              Se usa para calcular ROI, Cap Rate y patrimonio total del portafolio.
            </div>
          </div>

          <div>
            <Label>País</Label>
            <Select value={form.country} onValueChange={(v) => set("country", v)}>
              <SelectTrigger data-testid="property-form-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="AR">Argentina</SelectItem>
                <SelectItem value="US">EE.UU.</SelectItem>
                <SelectItem value="MX">México</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Ciudad</Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
          </div>
          <div>
            <Label>Empresa titular</Label>
            <Input value={form.company} onChange={(e) => set("company", e.target.value)} />
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="apartment">Departamento</SelectItem>
                <SelectItem value="house">Casa</SelectItem>
                <SelectItem value="garage">Cochera / Garage</SelectItem>
                <SelectItem value="commercial">Comercial</SelectItem>
                <SelectItem value="land">Terreno</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Estado</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="occupied">Ocupada</SelectItem>
                <SelectItem value="vacant">Vacía</SelectItem>
                <SelectItem value="maintenance">Mantenimiento</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Foto de portada</Label>
            <div className="rounded-lg border border-border/60 bg-card p-4 mt-1 space-y-3">
              {(pendingPreview || form.photo_gridfs_id || form.photo_url) && (
                <div className="relative w-full max-w-md">
                  <img
                    src={pendingPreview || (form.photo_gridfs_id ? `${API}/properties/${property?.id}/photo?v=${form.photo_gridfs_id}` : form.photo_url)}
                    alt="preview"
                    className="w-full aspect-[16/10] object-cover rounded-lg border border-border/60"
                  />
                  <button
                    type="button"
                    onClick={clearPhoto}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-rose-500 text-white grid place-items-center shadow-lg"
                  ><X className="w-4 h-4" /></button>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="property-photo-file"
                >
                  <Upload className="w-4 h-4 mr-1" /> Elegir de la biblioteca
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="property-photo-camera"
                >
                  <Camera className="w-4 h-4 mr-1" /> Tomar foto
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && onPhotoSelected(e.target.files[0])}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && onPhotoSelected(e.target.files[0])}
                />
              </div>
              {!property?.id && !pendingFile && (
                <div className="text-xs text-muted-foreground">💡 Podés elegir la foto ahora; se subirá al guardar la propiedad.</div>
              )}
              {pendingFile && (
                <div className="text-xs text-emerald-500">✓ Foto lista para subir al guardar.</div>
              )}
              <div className="text-[11px] text-muted-foreground pt-2 border-t border-border/50">
                <ImageIcon className="w-3 h-3 inline mr-1" />
                O pegá una URL externa (ej. Google Photos, Imgur, Unsplash):
              </div>
              <Input
                value={form.photo_url}
                onChange={(e) => { set("photo_url", e.target.value); set("photo_gridfs_id", ""); }}
                placeholder="https://…"
              />
            </div>
          </div>
          <div>
            <Label>Latitud</Label>
            <Input type="number" step="0.000001" value={form.latitude ?? ""} onChange={(e) => set("latitude", e.target.value === "" ? null : parseFloat(e.target.value))} />
          </div>
          <div>
            <Label>Longitud</Label>
            <Input type="number" step="0.000001" value={form.longitude ?? ""} onChange={(e) => set("longitude", e.target.value === "" ? null : parseFloat(e.target.value))} />
          </div>
          <div className="md:col-span-2">
            <Label>Notas</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
          <DialogFooter className="md:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="property-form-submit">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
